import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ChevronRight, MapPin, Truck, Shield, CreditCard, Star, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import ProductCard from '../components/ProductCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [deals, setDeals] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const [featuredRes, newArrivalsRes, dealsRes, allRes] = await Promise.all([
        axios.get(`${API}/products?featured=true&limit=8`),
        axios.get(`${API}/products?new_arrival=true&limit=8`),
        axios.get(`${API}/products?tags=$1-$10&limit=8`),
        axios.get(`${API}/products?limit=12`)
      ]);
      
      setFeaturedProducts(featuredRes.data);
      setNewArrivals(newArrivalsRes.data);
      setDeals(dealsRes.data);
      setAllProducts(allRes.data);
      
      // Best sellers - use featured or first 8 products sorted by reviews/popularity
      setBestSellers(featuredRes.data.length > 0 ? featuredRes.data.slice(0, 4) : allRes.data.slice(0, 4));
    } catch (error) {
      console.error('Failed to fetch products', error);
    }
  };

  const categories = [
    {
      name: 'Traditional Wear',
      subtitle: 'Authentic Nepali',
      image: 'https://images.pexels.com/photos/4338312/pexels-photo-4338312.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      link: '/category/traditional-wear'
    },
    {
      name: 'Jewelry',
      subtitle: 'Imitation & Cultural',
      image: 'https://images.pexels.com/photos/29502933/pexels-photo-29502933.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      link: '/category/jewelry'
    },
    {
      name: 'Clothing',
      subtitle: 'Casual & Everyday',
      image: 'https://images.pexels.com/photos/27575104/pexels-photo-27575104.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      link: '/category/clothing'
    },
    {
      name: 'Accessories',
      subtitle: 'Complete Your Look',
      image: 'https://images.pexels.com/photos/1152077/pexels-photo-1152077.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      link: '/category/accessories'
    }
  ];

  const quickLinks = [
    { name: 'Women\'s Clothing', link: '/category/clothing?gender=women' },
    { name: 'Men\'s Clothing', link: '/category/clothing?gender=men' },
    { name: 'New Arrivals', link: '/shop?new_arrival=true' },
    { name: 'Under $10 Deals', link: '/shop?max_price=10' },
  ];

  const trustBadges = [
    { icon: Truck, title: 'Free Shipping', desc: 'On orders over $75' },
    { icon: MapPin, title: 'Local Pickup', desc: 'Reynoldsburg, OH' },
    { icon: Shield, title: 'Secure Payment', desc: 'SSL Encrypted' },
    { icon: CreditCard, title: 'Easy Returns', desc: '7-day policy' }
  ];

  return (
    <div data-testid="home-page" className="bg-[#FAFAFA]">
      {/* Hero Section - Premium Full Width */}
      <section className="relative h-[85vh] min-h-[600px] max-h-[900px] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/28943676/pexels-photo-28943676.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Nepali Fashion"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-2xl">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-[#D4AF37] uppercase tracking-[0.3em] text-sm font-medium mb-6"
            >
              Authentic Nepali Heritage
            </motion.p>
            
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight"
              style={{ fontFamily: 'Playfair Display, serif' }}
              data-testid="hero-title"
            >
              Discover the Beauty of <span className="text-[#D4AF37]">Nepali Fashion</span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-lg text-white/90 mb-10 leading-relaxed"
              data-testid="hero-subtitle"
            >
              Curated clothing, jewelry, and accessories that celebrate culture, craftsmanship, and contemporary style.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link
                to="/shop"
                className="inline-flex items-center justify-center bg-[#D4AF37] text-white px-10 py-4 text-sm font-bold uppercase tracking-widest hover:bg-[#B5952F] transition-all duration-300"
                data-testid="hero-shop-button"
              >
                Shop Collection
                <ArrowRight className="ml-3 w-4 h-4" />
              </Link>
              <Link
                to="/shop?max_price=10"
                className="inline-flex items-center justify-center border-2 border-white text-white px-10 py-4 text-sm font-bold uppercase tracking-widest hover:bg-white hover:text-[#8B1F1F] transition-all duration-300"
                data-testid="hero-deals-button"
              >
                Under $10 Deals
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="bg-white border-b border-gray-100" data-testid="trust-badges">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-gray-100">
            {trustBadges.map((badge, index) => (
              <div key={index} className="flex items-center justify-center gap-4 py-6 px-4">
                <badge.icon className="w-8 h-8 text-[#D4AF37] flex-shrink-0" />
                <div>
                  <p className="font-bold text-sm text-gray-900">{badge.title}</p>
                  <p className="text-xs text-gray-500">{badge.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Shop by Category - Bento Grid */}
      <section className="py-20 md:py-28" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-sm font-medium mb-3">Explore</p>
              <h2
                className="text-3xl md:text-4xl font-bold text-gray-900"
                style={{ fontFamily: 'Playfair Display, serif' }}
                data-testid="categories-title"
              >
                Shop by Category
              </h2>
            </div>
            <Link to="/shop" className="mt-4 md:mt-0 text-[#8B1F1F] font-medium flex items-center hover:underline">
              View All Categories <ChevronRight className="w-4 h-4 ml-1" />
            </Link>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {categories.map((category) => (
              <Link
                key={category.name}
                to={category.link}
                className="group relative overflow-hidden aspect-[3/4]"
                data-testid={`category-card-${category.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <img
                  src={category.image}
                  alt={category.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                  <p className="text-[#D4AF37] text-[10px] md:text-xs uppercase tracking-widest mb-1">{category.subtitle}</p>
                  <h3 className="text-white text-lg md:text-2xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {category.name}
                  </h3>
                  <span className="inline-flex items-center text-white text-xs md:text-sm font-medium group-hover:underline">
                    Shop Now <ArrowRight className="w-3 h-3 md:w-4 md:h-4 ml-2 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            ))}
          </div>

          {/* Quick Links */}
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            {quickLinks.map((link) => (
              <Link
                key={link.name}
                to={link.link}
                className="px-6 py-2 border border-gray-300 text-sm hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
              >
                {link.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 md:py-28 bg-white" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-sm font-medium mb-3">Curated Selection</p>
              <h2
                className="text-3xl md:text-4xl font-bold text-gray-900"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Featured Products
              </h2>
            </div>
            <Link to="/shop?featured=true" className="mt-4 md:mt-0 text-[#8B1F1F] font-medium flex items-center hover:underline">
              View All <ChevronRight className="w-4 h-4 ml-1" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {(featuredProducts.length > 0 ? featuredProducts : allProducts).slice(0, 8).map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* Why Shop NeporaForYou */}
      <section className="py-16 bg-gradient-to-r from-[#1A1A1A] to-[#2D2D2D]" data-testid="why-shop-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2
              className="text-3xl md:text-4xl font-bold text-white mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Why Shop NeporaForYou?
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Discover authentic South Asian fashion with unmatched quality and service
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center p-6">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                <svg className="w-7 h-7 text-[#D4AF37]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-white font-semibold mb-2">Authentic Products</h3>
              <p className="text-gray-400 text-sm">Genuine items from Nepal & India</p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                <Truck className="w-7 h-7 text-[#D4AF37]" />
              </div>
              <h3 className="text-white font-semibold mb-2">Free Shipping</h3>
              <p className="text-gray-400 text-sm">On orders over $75</p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                <MapPin className="w-7 h-7 text-[#D4AF37]" />
              </div>
              <h3 className="text-white font-semibold mb-2">Free Local Pickup</h3>
              <p className="text-gray-400 text-sm">Reynoldsburg, OH</p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                <Shield className="w-7 h-7 text-[#D4AF37]" />
              </div>
              <h3 className="text-white font-semibold mb-2">Secure Checkout</h3>
              <p className="text-gray-400 text-sm">SSL encrypted payments</p>
            </div>
          </div>
        </div>
      </section>

      {/* Shop For Section - Replaces Occasions */}
      <section className="py-16 md:py-20 bg-[#FAFAFA]" data-testid="shop-for-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-sm font-medium mb-3">Popular Picks</p>
            <h2
              className="text-3xl md:text-4xl font-bold text-gray-900"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Shop For
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { name: 'Women', link: '/category/clothing?gender=women', icon: '👗' },
              { name: 'Men', link: '/category/clothing?gender=men', icon: '👔' },
              { name: 'Cultural Wear', link: '/category/traditional-wear', icon: '🪔' },
              { name: 'Under $10', link: '/shop?max_price=10', icon: '🏷️' },
            ].map((item) => (
              <Link
                key={item.name}
                to={item.link}
                className="group bg-white p-6 text-center hover:shadow-lg transition-all duration-300 border border-gray-100 hover:border-[#D4AF37]"
              >
                <div className="text-4xl mb-3 group-hover:scale-110 transition-transform duration-300">
                  {item.icon}
                </div>
                <h3 className="font-bold text-gray-900 text-sm uppercase tracking-wide">{item.name}</h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Best Sellers */}
      {bestSellers.length > 0 && (
        <section className="py-16 bg-white" data-testid="best-sellers-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10">
              <div>
                <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-sm font-medium mb-3">Customer Favorites</p>
                <h2
                  className="text-3xl md:text-4xl font-bold text-gray-900"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Best Sellers
                </h2>
              </div>
              <Link to="/shop?featured=true" className="mt-4 md:mt-0 text-[#8B1F1F] font-medium flex items-center hover:underline">
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {bestSellers.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Promotional Banner */}
      <section className="relative py-24 md:py-32 overflow-hidden" data-testid="promo-section">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/7600998/pexels-photo-7600998.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Promotion"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#8B1F1F]/90" />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#D4AF37] uppercase tracking-[0.3em] text-sm font-medium mb-4">Limited Time Offer</p>
          <h2
            className="text-3xl md:text-5xl font-bold text-white mb-6"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Free Shipping on Orders Over $75
          </h2>
          <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">
            Plus, enjoy free local pickup at our Reynoldsburg store. Shop authentic Nepali fashion today.
          </p>
          <Link
            to="/shop"
            className="inline-flex items-center justify-center bg-[#D4AF37] text-white px-12 py-4 text-sm font-bold uppercase tracking-widest hover:bg-[#B5952F] transition-all duration-300"
          >
            Shop Now <ArrowRight className="ml-3 w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* New Arrivals */}
      {newArrivals.length > 0 && (
        <section className="py-20 md:py-28 bg-white" data-testid="new-arrivals-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
              <div>
                <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-sm font-medium mb-3">Just In</p>
                <h2
                  className="text-3xl md:text-4xl font-bold text-gray-900"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  New Arrivals
                </h2>
              </div>
              <Link to="/shop?new_arrival=true" className="mt-4 md:mt-0 text-[#8B1F1F] font-medium flex items-center hover:underline">
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {newArrivals.slice(0, 4).map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Store Location */}
      <section className="py-20 md:py-28 bg-[#1A1A1A]" data-testid="location-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-sm font-medium mb-4">Visit Us</p>
              <h2
                className="text-3xl md:text-4xl font-bold text-white mb-6"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Our Store
              </h2>
              <div className="space-y-4 text-white/80">
                <p className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                  <span>
                    <strong className="text-white block">Nepora Clothing & Accessories</strong>
                    6575 E Livingston Ave<br />
                    Reynoldsburg, OH 43068
                  </span>
                </p>
                <p className="flex items-center gap-3">
                  <span className="text-[#D4AF37] text-xl">🕘</span>
                  <span>Open Daily: 9:00 AM – 9:00 PM</span>
                </p>
              </div>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <a
                  href="https://maps.google.com/?q=6575+E+Livingston+Ave+Reynoldsburg+OH+43068"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center border border-[#D4AF37] text-[#D4AF37] px-8 py-3 text-sm font-bold uppercase tracking-widest hover:bg-[#D4AF37] hover:text-white transition-all duration-300"
                >
                  Get Directions
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center border border-white/30 text-white px-8 py-3 text-sm font-bold uppercase tracking-widest hover:bg-white hover:text-[#1A1A1A] transition-all duration-300"
                >
                  Contact Us
                </Link>
              </div>
            </div>
            
            <div className="relative aspect-video md:aspect-square overflow-hidden">
              <img
                src="https://images.pexels.com/photos/1884581/pexels-photo-1884581.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Nepora Store"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* Customer Reviews */}
      <section className="py-20 md:py-28 bg-[#FAFAFA]" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-sm font-medium mb-3">Testimonials</p>
            <h2
              className="text-3xl md:text-4xl font-bold text-gray-900"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              What Our Customers Say
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { name: 'Priya S.', text: 'Beautiful sarees and amazing quality! The staff was so helpful in finding the perfect outfit for my wedding.', rating: 5 },
              { name: 'Anjali K.', text: 'Love the authentic Nepali jewelry collection. Prices are very reasonable and the craftsmanship is excellent.', rating: 5 },
              { name: 'Suman R.', text: 'Great selection of traditional wear. The local pickup option is super convenient. Highly recommend!', rating: 5 }
            ].map((review, index) => (
              <div key={index} className="bg-white p-8 border border-gray-100">
                <div className="flex gap-1 mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />
                  ))}
                </div>
                <p className="text-gray-600 mb-6 leading-relaxed">"{review.text}"</p>
                <p className="font-bold text-gray-900">{review.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Follow Us - Social Media */}
      {newArrivals.length > 0 && (
        <section className="py-16 bg-white" data-testid="new-arrivals-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10">
              <div>
                <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-sm font-medium mb-3">Fresh Styles</p>
                <h2
                  className="text-3xl md:text-4xl font-bold text-gray-900"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  New Arrivals
                </h2>
              </div>
              <Link to="/shop?new=true" className="mt-4 md:mt-0 text-[#8B1F1F] font-medium flex items-center hover:underline">
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {newArrivals.slice(0, 4).map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Newsletter */}
      <section className="py-20 md:py-28 bg-[#8B1F1F]" data-testid="newsletter-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-3xl md:text-4xl font-bold text-white mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Join the Nepora Family
          </h2>
          <p className="text-white/80 mb-8">
            Subscribe for exclusive offers, new arrivals, and cultural fashion inspiration.
          </p>
          <form className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-6 py-4 bg-white/10 border border-white/30 text-white placeholder:text-white/60 focus:outline-none focus:border-[#D4AF37]"
            />
            <button
              type="submit"
              className="px-8 py-4 bg-[#D4AF37] text-white font-bold uppercase tracking-widest hover:bg-[#B5952F] transition-colors"
            >
              Subscribe
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Home;
